// Zmanim_N "gridview" rendering helpers.
//
// Mirrors the Minyanim-Roll Grid Designer concept (Minyanim_Roll_Grid_Designer.vue
// + the roll grid rendering duplicated in Minyanim_Roll_Render.vue / Display.vue),
// but for Zmanim_N: each selected zman renders as a card whose layout comes from a
// chosen grid and whose cells are rich-text templates with {name}/{time}/{countdown}
// tokens substituted per zman.
//
// Kept as pure functions (no component `this`) so the live preview (Add/Edit
// Zmanim) and the live Display render byte-for-byte identically.
import { formatRollCountdown } from '@/utils/minyanRollEngine';

// Gap (px) between Zmanim_N cards: the .zmanim-n-display grid gap in Display.vue
// and the Add/Edit/Duplicate previews. The 2-column card centering uses it.
export const ZMANIM_N_CARD_GAP_PX = 10;

// Live-preview floor for a Custom column count (4+): each card stays at least
// this wide and the preview scrolls sideways (see getPreviewGridMinWidth).
export const ZMANIM_N_PREVIEW_MIN_CARD_WIDTH_PX = 120;

export const DEFAULT_ZMANIM_N_HIGHLIGHT = Object.freeze({
  enabled: true,
  minutesBefore: 15,
  minutesAfter: 10,
  colorBackground: true,
  colorText: false,
  backgroundColor: '#22c55e',
  textColorHighlight: '#ffffff',
  upcomingColor: '#22c55e',
  activeColor: '#22c55e',
  textColor: '#ffffff',
});

export const DEFAULT_ZMANIM_N_COUNTDOWN = Object.freeze({
  enabled: true,
  minutesBefore: 15,
  colorBackground: true,
  colorText: false,
  backgroundColor: '#ef4444',
  textColorCountdown: '#ffffff',
  color: '#ffffff',
});

function normalizeMinutes(value, fallback) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return fallback;
  return Math.max(0, minutes);
}

export function normalizeZmanimHighlightDesign(design = {}) {
  const full = design || {};
  const source = full.highlight || full || {};
  const legacy = full.upcomingHighlight || {};
  const backgroundColor = source.backgroundColor
    || source.activeColor
    || source.upcomingColor
    || source.color
    || full.upcomingHighlightColor
    || legacy.color
    || DEFAULT_ZMANIM_N_HIGHLIGHT.backgroundColor;
  const textColorHighlight = source.textColorHighlight
    || source.textColor
    || DEFAULT_ZMANIM_N_HIGHLIGHT.textColorHighlight;

  return {
    ...DEFAULT_ZMANIM_N_HIGHLIGHT,
    ...source,
    enabled: source.enabled ?? full.upcomingHighlightEnabled ?? legacy.enabled ?? DEFAULT_ZMANIM_N_HIGHLIGHT.enabled,
    minutesBefore: normalizeMinutes(source.minutesBefore, DEFAULT_ZMANIM_N_HIGHLIGHT.minutesBefore),
    minutesAfter: normalizeMinutes(source.minutesAfter, DEFAULT_ZMANIM_N_HIGHLIGHT.minutesAfter),
    backgroundColor,
    textColorHighlight,
    upcomingColor: source.upcomingColor || backgroundColor,
    activeColor: source.activeColor || backgroundColor,
    textColor: source.textColor || textColorHighlight,
    colorBackground: typeof source.colorBackground === 'boolean'
      ? source.colorBackground
      : DEFAULT_ZMANIM_N_HIGHLIGHT.colorBackground,
    colorText: typeof source.colorText === 'boolean'
      ? source.colorText
      : DEFAULT_ZMANIM_N_HIGHLIGHT.colorText,
  };
}

export function normalizeZmanimCountdownDesign(design = {}) {
  const full = design || {};
  const source = full.countdown || full || {};
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(source, key);
  const backgroundColor = hasOwn('backgroundColor')
    ? source.backgroundColor
    : (source.bgColor || DEFAULT_ZMANIM_N_COUNTDOWN.backgroundColor);
  const textColorCountdown = hasOwn('textColorCountdown')
    ? source.textColorCountdown
    : (source.textColor || source.color || DEFAULT_ZMANIM_N_COUNTDOWN.textColorCountdown);

  return {
    ...DEFAULT_ZMANIM_N_COUNTDOWN,
    ...source,
    enabled: source.enabled ?? DEFAULT_ZMANIM_N_COUNTDOWN.enabled,
    minutesBefore: normalizeMinutes(source.minutesBefore, DEFAULT_ZMANIM_N_COUNTDOWN.minutesBefore),
    backgroundColor,
    textColorCountdown,
    color: source.color || textColorCountdown,
    colorBackground: typeof source.colorBackground === 'boolean'
      ? source.colorBackground
      : DEFAULT_ZMANIM_N_COUNTDOWN.colorBackground,
    colorText: typeof source.colorText === 'boolean'
      ? source.colorText
      : DEFAULT_ZMANIM_N_COUNTDOWN.colorText,
  };
}

export function getZmanimHighlightAccentColor(highlight) {
  const hl = normalizeZmanimHighlightDesign(highlight);
  return hl.backgroundColor || hl.activeColor || hl.upcomingColor || DEFAULT_ZMANIM_N_HIGHLIGHT.backgroundColor;
}

export function getZmanimHighlightTextColor(highlight) {
  const hl = normalizeZmanimHighlightDesign(highlight);
  return hl.textColorHighlight || hl.textColor || DEFAULT_ZMANIM_N_HIGHLIGHT.textColorHighlight;
}

export function getZmanimHighlightStyle(highlight) {
  const hl = normalizeZmanimHighlightDesign(highlight);
  if (hl.enabled === false) return {};
  const style = {};
  if (hl.colorBackground) style.backgroundColor = getZmanimHighlightAccentColor(hl);
  const textColor = getZmanimHighlightTextColor(hl);
  if (textColor) style.color = textColor;
  return style;
}

export function isZmanimHighlightActive(dateTime, now, highlight) {
  const hl = normalizeZmanimHighlightDesign(highlight);
  if (hl.enabled === false || !dateTime || !now) return false;
  const target = new Date(dateTime).getTime();
  const current = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(target) || !Number.isFinite(current)) return false;
  const diffMs = target - current;
  if (diffMs <= 0) return false;
  const minutesBefore = Number(hl.minutesBefore);
  if (Number.isFinite(minutesBefore) && minutesBefore > 0 && diffMs > minutesBefore * 60000) {
    return false;
  }
  return true;
}

// Grid layouts — a copy of ROLL_GRID_LAYOUTS so a zman card's cells lay out
// exactly as arranged in the Grid Designer. The "none" (No Grid) layout has no
// entry here: it falls back to a single full-width cell, matching the roll.
export const ZMANIM_N_GRID_LAYOUTS = {
  'grid-2': { cells: 2, container: { gridTemplateColumns: '1fr' }, cellStyles: {} },
  'grid-3': { cells: 3, container: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }, cellStyles: {} },
  'grid-4': { cells: 4, container: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }, cellStyles: {} },
  'grid-5': {
    cells: 5,
    container: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    cellStyles: { 0: { gridColumn: '1 / -1' }, 4: { gridColumn: '1 / -1' } },
  },
  'grid-6': { cells: 6, container: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }, cellStyles: {} },
};

// Only these variables are offered in the Zmanim Grid Designer's Variable menu.
export const ZMANIM_N_GRID_VARIABLES = [
  { value: 'name', label: 'Name' },
  { value: 'time', label: 'Time' },
  { value: 'countdown', label: 'Countdown' },
];

// Default "No Grid" card template: {name} and {time} pre-filled, side by side
// for 1 row or stacked (name on top, time below) for 2 rows. This is the
// out-of-the-box card view content; the user then styles fonts/colours freely.
// 1 row: {name} and {time} split to opposite ends of the row (name on the right
// end in RTL, time on the left). A flex "split row" (data-split) so the split
// lands on the variable boundary, keeping each value's own words together.
const DEFAULT_GRID_CELL_1ROW =
  '<p dir="rtl" data-split="true" style="display: flex; align-items: center; gap: 12px">'
  + '<span style="flex: 1 1 0%; text-align: right">{name}</span>'
  + '<span style="flex: 1 1 0%; text-align: left">{time}</span></p>';
const DEFAULT_GRID_CELL_2ROW =
  '<p dir="rtl" style="text-align: center">{name}</p><p dir="rtl" style="text-align: center">{time}</p>';

export function buildZmanimDefaultGrid(rows) {
  const cell = Number(rows) === 2 ? DEFAULT_GRID_CELL_2ROW : DEFAULT_GRID_CELL_1ROW;
  return { layout: 'none', cells: [cell], cellPaddings: [{ v: 0, h: 0 }] };
}

// Whether a grid is still the untouched default template (No Grid, a single
// cell holding only the {name}/{time} tokens). Used so the "Time & Name Layout"
// toggle can re-seed the default arrangement without clobbering a grid the user
// has genuinely customized (a real grid layout, or extra text in the cell).
export function isDefaultZmanimGrid(grid) {
  if (!grid || grid.layout !== 'none') return false;
  const cells = Array.isArray(grid.cells) ? grid.cells : [];
  const populated = cells.filter((c) => c && String(c).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim());
  if (populated.length > 1) return false;
  const tokens = String(cells[0] || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .trim();
  return tokens === '' || tokens === '{name}{time}' || tokens === '{time}{name}';
}

const TOKEN_TEST_RE = /\{(name|time|countdown)\}/;

export function escapeZmanimHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Whether a single cell holds anything worth rendering (a token, an image, or
// visible text). Mirrors rollCellHasContent.
export function zmanimGridCellHasContent(html) {
  if (!html) return false;
  const raw = String(html);
  if (TOKEN_TEST_RE.test(raw)) return true;
  if (/<img\b/i.test(raw)) return true;
  const text = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return text.length > 0;
}

// Whether a design's grid should drive the card rendering at all. Mirrors
// rollHasGrid: a layout is set, there are cells, and at least one has content.
export function zmanimGridHasContent(grid) {
  if (!grid || !grid.layout) return false;
  if (!Array.isArray(grid.cells) || !grid.cells.length) return false;
  return grid.cells.some((cell) => zmanimGridCellHasContent(cell));
}

// The cells to render (padded/truncated to the layout's cell count).
export function getZmanimGridCells(grid) {
  const g = grid || {};
  const layout = ZMANIM_N_GRID_LAYOUTS[g.layout];
  const cells = Array.isArray(g.cells) ? g.cells : [];
  const count = layout ? layout.cells : cells.length;
  const out = [];
  for (let i = 0; i < count; i += 1) out.push(cells[i] || '');
  return out;
}

export function getZmanimGridContainerStyle(grid) {
  const layout = ZMANIM_N_GRID_LAYOUTS[grid && grid.layout];
  return {
    display: 'grid',
    gap: '8px',
    width: '100%',
    ...(layout ? layout.container : { gridTemplateColumns: '1fr' }),
  };
}

export function getZmanimGridCellStyle(grid, idx) {
  const layout = ZMANIM_N_GRID_LAYOUTS[grid && grid.layout];
  const style = { ...((layout && layout.cellStyles && layout.cellStyles[idx]) || {}) };
  // Per-cell { v, h } padding (px) set via the Grid Designer's V/H steppers.
  const pad = Array.isArray(grid && grid.cellPaddings) ? grid.cellPaddings[idx] : null;
  if (pad) {
    const clampPad = (n) => Math.min(99, Math.max(0, Math.round(Number(n)) || 0));
    style.padding = `${clampPad(pad.v)}px ${clampPad(pad.h)}px`;
  }
  return style;
}

// Inline CSS for the {countdown} badge, from the design's countdown colours.
// Mirrors getRollCountdownCss: countdown colours apply in list view only.
export function getZmanimCountdownCss(countdown) {
  const cd = normalizeZmanimCountdownDesign(countdown);
  if (cd.enabled === false) return '';
  if (cd.viewMode && cd.viewMode !== 'list') return '';
  let css = '';
  const textColor = cd.textColorCountdown || cd.color;
  if (textColor) css += `color:${textColor};`;
  if (cd.backgroundColor) {
    css += `background-color:${cd.backgroundColor};padding:1px 8px;border-radius:6px;display:inline-block;`;
  }
  return css;
}

// Substitute {name}/{time}/{countdown} into a cell's rich-text HTML. Values are
// HTML-escaped; {countdown} is wrapped in the countdown badge span when a
// countdownCss is supplied. Mirrors renderRollCellHtml.
//
// Each value is also wrapped in its own <span>, so inside a flex "split row"
// (data-split) each variable is a distinct flex item; the split lands on the
// variable boundary (a multi-word value stays intact as one end), not on the
// spaces inside a value. In a normal row the spans are inert inline text.
export function renderZmanimGridCellHtml(cellHtml, valueMap, options = {}) {
  const html = String(cellHtml || '');
  const values = {
    name: (valueMap && valueMap.name) || '',
    time: (valueMap && valueMap.time) || '',
    countdown: (valueMap && valueMap.countdown) || '',
  };
  const countdownCss = options.countdownCss || '';
  return html.replace(/\{(name|time|countdown)\}/g, (_, key) => {
    const value = escapeZmanimHtml(values[key] || '');
    if (!value) return '';
    if (key === 'countdown' && countdownCss) {
      return `<span style="${countdownCss}">${value}</span>`;
    }
    return `<span>${value}</span>`;
  });
}

// Index of the soonest still-upcoming zman (positive time remaining), or -1.
// Countdown only shows on this zman, mirroring the roll (next minyan only).
export function getNearestFutureZmanIndex(items, now) {
  if (!Array.isArray(items) || !now) return -1;
  const current = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(current)) return -1;
  let idx = -1;
  let best = Infinity;
  items.forEach((item, i) => {
    const t = item && item.dateTime ? new Date(item.dateTime).getTime() : NaN;
    if (!Number.isFinite(t)) return;
    const diff = t - current;
    if (diff > 0 && diff < best) {
      best = diff;
      idx = i;
    }
  });
  return idx;
}

// HH:MM:SS remaining until a zman's dateTime, or '' when it has passed / is
// outside an optional `minutesBefore` window. Uses absolute epoch time so it is
// timezone-safe (both instants are absolute).
export function getZmanimCountdownString(dateTime, now, options = {}) {
  if (!dateTime || !now) return '';
  const countdown = normalizeZmanimCountdownDesign(options);
  if (countdown.enabled === false) return '';
  const target = new Date(dateTime).getTime();
  const current = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(target) || !Number.isFinite(current)) return '';
  const diffMs = target - current;
  if (diffMs <= 0) return '';
  const minutesBefore = Number(options.minutesBefore ?? countdown.minutesBefore);
  if (Number.isFinite(minutesBefore) && minutesBefore > 0 && diffMs > minutesBefore * 60000) {
    return '';
  }
  return formatRollCountdown(diffMs / 1000);
}
